import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { convertEstimate, deletePhoto, getEstimate, getSketchUploadUrl, getUploadUrl, listPhotos, recordPhoto, updateEstimate, } from './api';
import { DownloadPDFButton } from './EstimatePDF';
import { LineItemEditor } from './LineItemEditor';
import { SketchCanvas } from './SketchCanvas';
function statusBadgeStyle(status) {
    switch (status) {
        case 'accepted':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(52,199,89,0.12)',
                color: '#248a3d',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        case 'rejected':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(255,59,48,0.12)',
                color: '#c0392b',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        case 'converted':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(0,122,255,0.12)',
                color: '#0071e3',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        default:
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(142,142,147,0.15)',
                color: '#86868b',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
    }
}
export const EstimateDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [estimate, setEstimate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    // Editing state
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        siteAddress: { street: '', city: '', state: '', zip: '' },
    });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    // Line items state
    const [lineItems, setLineItems] = useState([]);
    const [lineItemsSaving, setLineItemsSaving] = useState(false);
    const [lineItemsError, setLineItemsError] = useState(null);
    const [lineItemsSaved, setLineItemsSaved] = useState(false);
    // Photos state
    const [photos, setPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoError, setPhotoError] = useState(null);
    const [lightboxPhoto, setLightboxPhoto] = useState(null);
    const fileInputRef = useRef(null);
    // Sketch state
    const [sketchSaving, setSketchSaving] = useState(false);
    const [sketchError, setSketchError] = useState(null);
    // Status change state
    const [statusChanging, setStatusChanging] = useState(false);
    const [statusError, setStatusError] = useState(null);
    // Convert state
    const [converting, setConverting] = useState(false);
    const [convertError, setConvertError] = useState(null);
    const loadEstimate = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const data = await getEstimate(id);
            setEstimate(data);
            setLineItems(data.lineItems ?? []);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load estimate');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => {
        loadEstimate();
    }, [loadEstimate]);
    const loadPhotos = useCallback(async () => {
        if (!id)
            return;
        setPhotosLoading(true);
        setPhotoError(null);
        try {
            const data = await listPhotos(id);
            // Show sketches first, then regular photos
            const sketches = data.filter((p) => p.uploadedBy === 'sketch')
                .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
            const regular = data.filter((p) => p.uploadedBy !== 'sketch');
            setPhotos([...sketches, ...regular]);
        }
        catch (e) {
            setPhotoError(e.message ?? 'Failed to load photos');
        }
        finally {
            setPhotosLoading(false);
        }
    }, [id]);
    useEffect(() => {
        if (activeTab === 'photos') {
            loadPhotos();
        }
    }, [activeTab, loadPhotos]);
    // Open edit mode
    const startEditing = () => {
        if (!estimate)
            return;
        setEditForm({
            customerName: estimate.customerName,
            customerId: estimate.customerId,
            siteAddress: { ...estimate.siteAddress },
            projectDescription: estimate.projectDescription,
            requirements: estimate.requirements,
            measurements: [...(estimate.measurements ?? [])],
            notes: estimate.notes,
            status: estimate.status,
        });
        setSaveError(null);
        setEditing(true);
    };
    const handleEditSave = async () => {
        if (!id || !estimate)
            return;
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateEstimate(id, {
                customerName: editForm.customerName,
                customerId: editForm.customerId,
                siteAddress: editForm.siteAddress,
                projectDescription: editForm.projectDescription,
                requirements: editForm.requirements,
                measurements: editForm.measurements,
                notes: editForm.notes,
                status: editForm.status,
            });
            setEstimate(updated);
            setEditing(false);
        }
        catch (e) {
            setSaveError(e.message ?? 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const handleLineItemsSave = async () => {
        if (!id)
            return;
        const laborTotal = lineItems
            .filter((li) => li.type === 'labor')
            .reduce((sum, li) => sum + li.qty * li.unitPrice, 0);
        const materialTotal = lineItems
            .filter((li) => li.type === 'material')
            .reduce((sum, li) => sum + li.qty * li.unitPrice, 0);
        setLineItemsSaving(true);
        setLineItemsError(null);
        try {
            const updated = await updateEstimate(id, {
                lineItems,
                laborTotal,
                materialTotal,
                totalAmount: laborTotal + materialTotal,
            });
            setEstimate(updated);
            setLineItemsSaved(true);
            setTimeout(() => setLineItemsSaved(false), 2000);
        }
        catch (e) {
            setLineItemsError(e.message ?? 'Failed to save line items');
        }
        finally {
            setLineItemsSaving(false);
        }
    };
    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files ?? []);
        if (!files.length || !id)
            return;
        setPhotoUploading(true);
        setPhotoError(null);
        try {
            for (const file of files) {
                const { photoId, s3Key, uploadUrl } = await getUploadUrl(id, file.name, file.type);
                await fetch(uploadUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type },
                });
                await recordPhoto(id, photoId, s3Key, 'user');
            }
            await loadPhotos();
        }
        catch (e) {
            setPhotoError(e.message ?? 'Failed to upload photo');
        }
        finally {
            setPhotoUploading(false);
            if (fileInputRef.current)
                fileInputRef.current.value = '';
        }
    };
    const handleDeletePhoto = async (photoId) => {
        if (!id)
            return;
        try {
            await deletePhoto(id, photoId);
            setPhotos((prev) => prev.filter((p) => p.photoId !== photoId));
        }
        catch (e) {
            setPhotoError(e.message ?? 'Failed to delete photo');
        }
    };
    const handleSketchSave = async (blob) => {
        if (!id)
            return;
        setSketchSaving(true);
        setSketchError(null);
        try {
            const { uploadUrl, s3Key } = await getSketchUploadUrl(id);
            await fetch(uploadUrl, {
                method: 'PUT',
                body: blob,
                headers: { 'Content-Type': 'image/png' },
            });
            // Record sketch as a photo so it shows in the Photos tab
            const photoId = crypto.randomUUID();
            await recordPhoto(id, photoId, s3Key, 'sketch');
        }
        catch (e) {
            setSketchError(e.message ?? 'Failed to save sketch');
        }
        finally {
            setSketchSaving(false);
        }
    };
    const handleConvert = async () => {
        if (!id || !estimate)
            return;
        setConverting(true);
        setConvertError(null);
        try {
            const updated = await convertEstimate(id);
            setEstimate(updated);
        }
        catch (e) {
            setConvertError(e.message ?? 'Failed to convert estimate');
        }
        finally {
            setConverting(false);
        }
    };
    const updateMeasurement = (idx, field, value) => {
        const measurements = [...(editForm.measurements ?? [])];
        measurements[idx] = { ...measurements[idx], [field]: value };
        setEditForm((prev) => ({ ...prev, measurements }));
    };
    const addMeasurement = () => {
        const measurements = [...(editForm.measurements ?? []), { label: '', value: '', unit: '' }];
        setEditForm((prev) => ({ ...prev, measurements }));
    };
    const removeMeasurement = (idx) => {
        const measurements = (editForm.measurements ?? []).filter((_, i) => i !== idx);
        setEditForm((prev) => ({ ...prev, measurements }));
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: { display: 'flex', justifyContent: 'center', padding: '80px 0' }, children: _jsx("div", { style: {
                        width: '32px',
                        height: '32px',
                        border: '3px solid rgba(0,0,0,0.1)',
                        borderTopColor: '#0071e3',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                    } }) }) }));
    }
    if (error || !estimate) {
        return (_jsx(Layout, { children: _jsxs("div", { style: { padding: '40px' }, children: [_jsx("p", { style: { color: '#c0392b' }, children: error ?? 'Estimate not found' }), _jsx("button", { onClick: () => navigate('/estimates'), style: {
                            marginTop: '12px',
                            padding: '10px 20px',
                            backgroundColor: '#0071e3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '980px',
                            cursor: 'pointer',
                        }, children: "Back to Estimates" })] }) }));
    }
    const sectionCardStyle = {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        padding: isMobile ? '20px 16px' : '28px 32px',
        marginBottom: '20px',
    };
    const inputStyle = {
        width: '100%',
        padding: '11px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    };
    const labelStyle = {
        fontSize: '13px',
        fontWeight: '600',
        color: '#86868b',
        display: 'block',
        marginBottom: '4px',
    };
    const fieldStyle = {
        marginBottom: '16px',
    };
    const readFieldStyle = {
        marginBottom: '16px',
    };
    const readLabel = {
        fontSize: '12px',
        color: '#86868b',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        marginBottom: '3px',
    };
    const readValue = {
        fontSize: '15px',
        color: '#1d1d1f',
    };
    const TABS = [
        { id: 'overview', label: 'Overview' },
        { id: 'lineitems', label: 'Line Items' },
        { id: 'photos', label: 'Photos' },
        { id: 'sketch', label: 'Sketch' },
        { id: 'pdf', label: 'PDF' },
    ];
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("button", { style: {
                                background: 'none',
                                border: 'none',
                                color: '#0071e3',
                                fontSize: '14px',
                                cursor: 'pointer',
                                padding: '0 0 8px 0',
                            }, onClick: () => navigate('/estimates'), children: "\u2190 Back to Estimates" }), _jsxs("div", { style: {
                                display: 'flex',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                justifyContent: 'space-between',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: '12px',
                            }, children: [_jsxs("div", { children: [_jsx("h2", { style: {
                                                fontSize: isMobile ? '28px' : '42px',
                                                fontWeight: '700',
                                                margin: '0 0 6px 0',
                                                color: '#1d1d1f',
                                                letterSpacing: '-0.5px',
                                            }, children: estimate.customerName }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }, children: [estimate.status === 'converted' ? (_jsx("span", { style: statusBadgeStyle(estimate.status), children: "Converted" })) : (_jsxs("select", { value: estimate.status, disabled: statusChanging, onChange: async (e) => {
                                                        const newStatus = e.target.value;
                                                        setStatusChanging(true);
                                                        setStatusError(null);
                                                        try {
                                                            const updated = await updateEstimate(id, { status: newStatus });
                                                            setEstimate(updated);
                                                        }
                                                        catch (err) {
                                                            setStatusError(err.message ?? 'Failed to update status');
                                                        }
                                                        finally {
                                                            setStatusChanging(false);
                                                        }
                                                    }, style: {
                                                        ...statusBadgeStyle(estimate.status),
                                                        border: '1px solid rgba(0,0,0,0.15)',
                                                        cursor: statusChanging ? 'wait' : 'pointer',
                                                        appearance: 'none',
                                                        WebkitAppearance: 'none',
                                                        paddingRight: '28px',
                                                        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%2386868b\'/%3E%3C/svg%3E")',
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: 'right 10px center',
                                                        backgroundSize: '8px',
                                                    }, children: [_jsx("option", { value: "draft", children: "Draft" }), _jsx("option", { value: "accepted", children: "Accepted" }), _jsx("option", { value: "rejected", children: "Rejected" })] })), statusError && (_jsx("span", { style: { fontSize: '12px', color: '#c0392b' }, children: statusError })), _jsxs("span", { style: { fontSize: '14px', color: '#86868b' }, children: ["Created ", new Date(estimate.createdAt).toLocaleDateString()] })] })] }), _jsxs("div", { style: { display: 'flex', gap: '10px', flexShrink: 0 }, children: [!editing && activeTab === 'overview' && (_jsx("button", { onClick: startEditing, style: {
                                                padding: '10px 22px',
                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                border: '1px solid rgba(0,0,0,0.12)',
                                                borderRadius: '980px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#1d1d1f',
                                                cursor: 'pointer',
                                                minHeight: '44px',
                                            }, children: "Edit" })), !estimate.convertedToProjectId && estimate.status !== 'converted' && (_jsx("button", { onClick: estimate.status === 'accepted' ? handleConvert : undefined, disabled: converting || estimate.status !== 'accepted', title: estimate.status !== 'accepted'
                                                ? 'Set status to Accepted to convert to a project'
                                                : undefined, style: {
                                                padding: '10px 22px',
                                                backgroundColor: converting || estimate.status !== 'accepted' ? '#86868b' : '#0071e3',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '980px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: converting || estimate.status !== 'accepted' ? 'not-allowed' : 'pointer',
                                                minHeight: '44px',
                                                opacity: estimate.status !== 'accepted' ? 0.55 : 1,
                                            }, children: converting ? 'Converting…' : 'Convert to Project' }))] })] }), convertError && (_jsx("p", { style: { color: '#c0392b', fontSize: '14px', marginTop: '8px' }, children: convertError })), estimate.convertedToProjectId && (_jsxs("div", { style: {
                                marginTop: '10px',
                                padding: '12px 16px',
                                backgroundColor: 'rgba(52,199,89,0.08)',
                                border: '1px solid rgba(52,199,89,0.2)',
                                borderRadius: '12px',
                                fontSize: '14px',
                                color: '#248a3d',
                            }, children: ["Converted to project.", ' ', _jsx("button", { style: {
                                        background: 'none',
                                        border: 'none',
                                        color: '#0071e3',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        padding: 0,
                                    }, onClick: () => navigate(`/projects/${estimate.convertedToProjectId}`), children: "View Project \u2192" })] }))] }), _jsx("div", { style: {
                        display: 'flex',
                        gap: '8px',
                        marginBottom: '20px',
                        overflowX: isMobile ? 'auto' : 'visible',
                        WebkitOverflowScrolling: 'touch',
                    }, children: TABS.map((tab) => (_jsx("button", { onClick: () => setActiveTab(tab.id), style: {
                            padding: '9px 20px',
                            backgroundColor: activeTab === tab.id ? '#0071e3' : 'rgba(255,255,255,0.7)',
                            color: activeTab === tab.id ? '#fff' : '#3c3c43',
                            border: '1px solid ' + (activeTab === tab.id ? '#0071e3' : 'rgba(0,0,0,0.1)'),
                            borderRadius: '980px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            minHeight: '40px',
                            transition: 'all 0.15s ease',
                        }, children: tab.label }, tab.id))) }), activeTab === 'overview' && (_jsx("div", { children: editing ? (
                    /* Edit form */
                    _jsxs("div", { children: [_jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                            fontSize: '17px',
                                            fontWeight: '600',
                                            color: '#1d1d1f',
                                            margin: '0 0 20px 0',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        }, children: "Customer & Address" }), _jsxs("div", { style: {
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                            gap: '16px',
                                        }, children: [_jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Customer Name" }), _jsx("input", { style: inputStyle, value: editForm.customerName ?? '', onChange: (e) => setEditForm((p) => ({ ...p, customerName: e.target.value })) })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Customer ID" }), _jsx("input", { style: inputStyle, value: editForm.customerId ?? '', onChange: (e) => setEditForm((p) => ({ ...p, customerId: e.target.value })) })] })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Street" }), _jsx("input", { style: inputStyle, value: editForm.siteAddress?.street ?? '', onChange: (e) => setEditForm((p) => ({
                                                    ...p,
                                                    siteAddress: { ...p.siteAddress, street: e.target.value },
                                                })) })] }), _jsxs("div", { style: {
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr',
                                            gap: '16px',
                                        }, children: [_jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "City" }), _jsx("input", { style: inputStyle, value: editForm.siteAddress?.city ?? '', onChange: (e) => setEditForm((p) => ({
                                                            ...p,
                                                            siteAddress: { ...p.siteAddress, city: e.target.value },
                                                        })) })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "State" }), _jsx("input", { style: inputStyle, value: editForm.siteAddress?.state ?? '', onChange: (e) => setEditForm((p) => ({
                                                            ...p,
                                                            siteAddress: { ...p.siteAddress, state: e.target.value },
                                                        })), maxLength: 2 })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "ZIP" }), _jsx("input", { style: inputStyle, value: editForm.siteAddress?.zip ?? '', onChange: (e) => setEditForm((p) => ({
                                                            ...p,
                                                            siteAddress: { ...p.siteAddress, zip: e.target.value },
                                                        })) })] })] })] }), _jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                            fontSize: '17px',
                                            fontWeight: '600',
                                            color: '#1d1d1f',
                                            margin: '0 0 20px 0',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        }, children: "Project Details" }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Status" }), _jsxs("select", { style: { ...inputStyle, cursor: 'pointer' }, value: editForm.status ?? 'draft', onChange: (e) => setEditForm((p) => ({ ...p, status: e.target.value })), children: [_jsx("option", { value: "draft", children: "Draft" }), _jsx("option", { value: "accepted", children: "Accepted" }), _jsx("option", { value: "rejected", children: "Rejected" }), _jsx("option", { value: "converted", children: "Converted" })] })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Project Description" }), _jsx("textarea", { style: { ...inputStyle, minHeight: '90px', resize: 'vertical' }, value: editForm.projectDescription ?? '', onChange: (e) => setEditForm((p) => ({ ...p, projectDescription: e.target.value })) })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Requirements" }), _jsx("textarea", { style: { ...inputStyle, minHeight: '70px', resize: 'vertical' }, value: editForm.requirements ?? '', onChange: (e) => setEditForm((p) => ({ ...p, requirements: e.target.value })) })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("label", { style: labelStyle, children: "Notes" }), _jsx("textarea", { style: { ...inputStyle, minHeight: '70px', resize: 'vertical' }, value: editForm.notes ?? '', onChange: (e) => setEditForm((p) => ({ ...p, notes: e.target.value })) })] })] }), _jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                            fontSize: '17px',
                                            fontWeight: '600',
                                            color: '#1d1d1f',
                                            margin: '0 0 20px 0',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        }, children: "Measurements" }), (editForm.measurements ?? []).map((m, idx) => (_jsxs("div", { style: {
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr auto',
                                            gap: '10px',
                                            marginBottom: '10px',
                                            alignItems: 'end',
                                        }, children: [_jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Label" }), _jsx("input", { style: inputStyle, value: m.label, onChange: (e) => updateMeasurement(idx, 'label', e.target.value) })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Value" }), _jsx("input", { style: inputStyle, value: m.value, onChange: (e) => updateMeasurement(idx, 'value', e.target.value) })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Unit" }), _jsx("input", { style: inputStyle, value: m.unit, onChange: (e) => updateMeasurement(idx, 'unit', e.target.value) })] }), _jsx("button", { onClick: () => removeMeasurement(idx), style: {
                                                    padding: '11px 14px',
                                                    backgroundColor: 'rgba(255,59,48,0.1)',
                                                    color: '#c0392b',
                                                    border: 'none',
                                                    borderRadius: '12px',
                                                    cursor: 'pointer',
                                                    fontWeight: '600',
                                                    minHeight: '44px',
                                                }, children: "\u00D7" })] }, idx))), _jsx("button", { onClick: addMeasurement, style: {
                                            padding: '10px 20px',
                                            backgroundColor: 'rgba(0,113,227,0.08)',
                                            color: '#0071e3',
                                            border: '1px dashed rgba(0,113,227,0.3)',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            minHeight: '44px',
                                        }, children: "+ Add Measurement" })] }), saveError && (_jsx("p", { style: { color: '#c0392b', fontSize: '14px', marginBottom: '12px' }, children: saveError })), _jsxs("div", { style: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginBottom: '40px' }, children: [_jsx("button", { onClick: () => setEditing(false), style: {
                                            padding: '12px 24px',
                                            backgroundColor: 'rgba(255,255,255,0.7)',
                                            border: '1px solid rgba(0,0,0,0.12)',
                                            borderRadius: '980px',
                                            fontSize: '15px',
                                            fontWeight: '500',
                                            color: '#1d1d1f',
                                            cursor: 'pointer',
                                            minHeight: '44px',
                                        }, children: "Cancel" }), _jsx("button", { onClick: handleEditSave, disabled: saving, style: {
                                            padding: '12px 28px',
                                            backgroundColor: saving ? '#86868b' : '#0071e3',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '980px',
                                            fontSize: '15px',
                                            fontWeight: '600',
                                            cursor: saving ? 'not-allowed' : 'pointer',
                                            minHeight: '44px',
                                        }, children: saving ? 'Saving…' : 'Save Changes' })] })] })) : (
                    /* Read mode */
                    _jsxs("div", { children: [_jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                            fontSize: '17px',
                                            fontWeight: '600',
                                            color: '#1d1d1f',
                                            margin: '0 0 20px 0',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        }, children: "Customer & Site" }), _jsxs("div", { style: {
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                            gap: '16px',
                                        }, children: [_jsxs("div", { style: readFieldStyle, children: [_jsx("div", { style: readLabel, children: "Customer Name" }), _jsx("div", { style: readValue, children: estimate.customerName })] }), estimate.customerId && (_jsxs("div", { style: readFieldStyle, children: [_jsx("div", { style: readLabel, children: "Customer ID" }), _jsx("div", { style: readValue, children: estimate.customerId })] })), _jsxs("div", { style: readFieldStyle, children: [_jsx("div", { style: readLabel, children: "Site Address" }), _jsxs("div", { style: readValue, children: [estimate.siteAddress.street, _jsx("br", {}), estimate.siteAddress.city, ", ", estimate.siteAddress.state, ' ', estimate.siteAddress.zip] })] })] })] }), _jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                            fontSize: '17px',
                                            fontWeight: '600',
                                            color: '#1d1d1f',
                                            margin: '0 0 20px 0',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        }, children: "Project Details" }), _jsxs("div", { style: readFieldStyle, children: [_jsx("div", { style: readLabel, children: "Description" }), _jsx("div", { style: { ...readValue, whiteSpace: 'pre-wrap' }, children: estimate.projectDescription })] }), estimate.requirements && (_jsxs("div", { style: readFieldStyle, children: [_jsx("div", { style: readLabel, children: "Requirements" }), _jsx("div", { style: { ...readValue, whiteSpace: 'pre-wrap' }, children: estimate.requirements })] })), estimate.notes && (_jsxs("div", { style: readFieldStyle, children: [_jsx("div", { style: readLabel, children: "Notes" }), _jsx("div", { style: { ...readValue, whiteSpace: 'pre-wrap' }, children: estimate.notes })] }))] }), estimate.measurements && estimate.measurements.length > 0 && (_jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                            fontSize: '17px',
                                            fontWeight: '600',
                                            color: '#1d1d1f',
                                            margin: '0 0 16px 0',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        }, children: "Measurements" }), _jsx("div", { style: {
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
                                            gap: '12px',
                                        }, children: estimate.measurements.map((m, idx) => (_jsxs("div", { style: {
                                                padding: '12px 14px',
                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                borderRadius: '10px',
                                            }, children: [_jsx("div", { style: { fontSize: '12px', color: '#86868b', marginBottom: '2px' }, children: m.label }), _jsxs("div", { style: { fontSize: '18px', fontWeight: '700', color: '#1d1d1f' }, children: [m.value, ' ', _jsx("span", { style: { fontSize: '13px', fontWeight: '400', color: '#86868b' }, children: m.unit })] })] }, idx))) })] })), _jsx("div", { style: sectionCardStyle, children: _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }, children: [_jsxs("div", { children: [_jsx("div", { style: readLabel, children: "Labor Total" }), _jsxs("div", { style: { fontSize: '20px', fontWeight: '700', color: '#248a3d' }, children: ["$", estimate.laborTotal.toFixed(2)] })] }), _jsxs("div", { children: [_jsx("div", { style: readLabel, children: "Material Total" }), _jsxs("div", { style: { fontSize: '20px', fontWeight: '700', color: '#0071e3' }, children: ["$", estimate.materialTotal.toFixed(2)] })] }), _jsxs("div", { children: [_jsx("div", { style: readLabel, children: "Grand Total" }), _jsxs("div", { style: { fontSize: '24px', fontWeight: '700', color: '#1d1d1f' }, children: ["$", estimate.totalAmount.toFixed(2)] })] })] }) })] })) })), activeTab === 'lineitems' && (_jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                fontSize: '17px',
                                fontWeight: '600',
                                color: '#1d1d1f',
                                margin: '0 0 20px 0',
                                paddingBottom: '12px',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                            }, children: "Line Items" }), _jsx(LineItemEditor, { lineItems: lineItems, onChange: setLineItems }), lineItemsError && (_jsx("p", { style: { color: '#c0392b', fontSize: '14px', marginTop: '12px' }, children: lineItemsError })), _jsxs("div", { style: {
                                marginTop: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }, children: [_jsx("button", { onClick: handleLineItemsSave, disabled: lineItemsSaving, style: {
                                        padding: '12px 28px',
                                        backgroundColor: lineItemsSaving ? '#86868b' : '#0071e3',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '980px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: lineItemsSaving ? 'not-allowed' : 'pointer',
                                        minHeight: '44px',
                                    }, children: lineItemsSaving ? 'Saving…' : 'Save Line Items' }), lineItemsSaved && (_jsx("span", { style: { fontSize: '14px', color: '#248a3d', fontWeight: '500' }, children: "\u2713 Saved" }))] })] })), activeTab === 'photos' && (_jsx("div", { children: _jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                    fontSize: '17px',
                                    fontWeight: '600',
                                    color: '#1d1d1f',
                                    margin: '0 0 20px 0',
                                    paddingBottom: '12px',
                                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                                }, children: "Photos" }), photoError && (_jsx("p", { style: { color: '#c0392b', fontSize: '14px', marginBottom: '12px' }, children: photoError })), _jsxs("div", { style: { marginBottom: '20px' }, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", multiple: true, style: { display: 'none' }, onChange: handlePhotoUpload }), _jsx("button", { onClick: () => fileInputRef.current?.click(), disabled: photoUploading, style: {
                                            padding: '11px 24px',
                                            backgroundColor: photoUploading ? '#86868b' : '#0071e3',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '980px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: photoUploading ? 'not-allowed' : 'pointer',
                                            minHeight: '44px',
                                        }, children: photoUploading ? 'Uploading…' : '+ Upload Photos' })] }), photosLoading && (_jsx("p", { style: { color: '#86868b', fontSize: '14px' }, children: "Loading photos\u2026" })), !photosLoading && photos.length === 0 && (_jsx("p", { style: { color: '#86868b', fontSize: '14px' }, children: "No photos uploaded yet." })), photos.length > 0 && (_jsx("div", { style: {
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(auto-fill, minmax(120px, 1fr))`,
                                    gap: '10px',
                                }, children: photos.map((photo) => (_jsxs("div", { style: { position: 'relative', borderRadius: '10px', overflow: 'hidden' }, children: [_jsx("img", { src: photo.url ?? '', alt: photo.uploadedBy === 'sketch' ? 'Sketch' : 'Estimate photo', style: {
                                                width: '100%',
                                                height: '120px',
                                                objectFit: 'cover',
                                                display: 'block',
                                                cursor: 'pointer',
                                                borderRadius: '10px',
                                            }, onClick: () => setLightboxPhoto(photo) }), photo.uploadedBy === 'sketch' && (_jsx("span", { style: {
                                                position: 'absolute',
                                                bottom: '4px',
                                                left: '4px',
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                color: '#fff',
                                                fontSize: '10px',
                                                fontWeight: '700',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                letterSpacing: '0.04em',
                                            }, children: "SKETCH" })), photo.uploadedBy !== 'sketch' && (_jsx("button", { onClick: () => handleDeletePhoto(photo.photoId), style: {
                                                position: 'absolute',
                                                top: '4px',
                                                right: '4px',
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                color: '#fff',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                lineHeight: 1,
                                            }, children: "\u00D7" }))] }, photo.photoId))) }))] }) })), activeTab === 'sketch' && (_jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                fontSize: '17px',
                                fontWeight: '600',
                                color: '#1d1d1f',
                                margin: '0 0 4px 0',
                                paddingBottom: '12px',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                            }, children: "Sketch Pad" }), _jsx("p", { style: { fontSize: '13px', color: '#86868b', marginBottom: '16px' }, children: "Each sketch you save is added to the Photos tab for review." }), sketchError && (_jsx("p", { style: { color: '#c0392b', fontSize: '14px', marginBottom: '12px' }, children: sketchError })), sketchSaving && (_jsx("p", { style: { color: '#86868b', fontSize: '14px', marginBottom: '8px' }, children: "Saving sketch\u2026" })), _jsx(SketchCanvas, { onSave: handleSketchSave })] })), activeTab === 'pdf' && (_jsxs("div", { style: sectionCardStyle, children: [_jsx("h3", { style: {
                                fontSize: '17px',
                                fontWeight: '600',
                                color: '#1d1d1f',
                                margin: '0 0 20px 0',
                                paddingBottom: '12px',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                            }, children: "Export PDF" }), _jsx("p", { style: { fontSize: '15px', color: '#86868b', marginBottom: '20px' }, children: "Generate and download a professional PDF estimate to share with your customer." }), _jsx("div", { style: { marginBottom: '24px' }, children: _jsx(DownloadPDFButton, { estimate: estimate }) }), _jsx("div", { style: {
                                padding: '16px',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                borderRadius: '12px',
                                border: '1px solid rgba(0,0,0,0.06)',
                            }, children: _jsx("p", { style: { fontSize: '13px', color: '#86868b', margin: 0 }, children: "PDF includes: customer info, site address, project description, requirements, all line items, and totals." }) })] })), lightboxPhoto && (_jsxs("div", { style: {
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }, onClick: () => setLightboxPhoto(null), children: [_jsx("img", { src: lightboxPhoto.url ?? '', alt: "Full size", style: {
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: '12px',
                            }, onClick: (e) => e.stopPropagation() }), _jsx("button", { onClick: () => setLightboxPhoto(null), style: {
                                position: 'fixed',
                                top: '20px',
                                right: '20px',
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: "\u00D7" })] }))] }) }));
};
//# sourceMappingURL=EstimateDetail.js.map